import type { PropsWithChildren, ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Panel } from "@/components/ui/panel";

interface AuthLayoutProps extends PropsWithChildren {
  eyebrow: string;
  title: string;
  subtitle: string;
  footer?: ReactNode;
}

export function AuthLayout({
  eyebrow,
  title,
  subtitle,
  footer,
  children,
}: AuthLayoutProps) {
  const { t } = useTranslation();

  return (
    <main className="relative min-h-screen overflow-hidden px-4 py-8 sm:px-6 lg:px-10">
      <div className="absolute inset-0 bg-grain-radial opacity-90" aria-hidden />
      <div
        className="absolute left-[-6rem] top-16 h-64 w-64 rounded-full bg-clay/10 blur-3xl"
        aria-hidden
      />
      <div
        className="absolute bottom-0 right-[-5rem] h-72 w-72 rounded-full bg-olive/10 blur-3xl"
        aria-hidden
      />

      <div className="relative mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="space-y-6 pr-0 lg:pr-8">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-clay">
            {eyebrow}
          </p>
          <h1 className="max-w-2xl font-display text-5xl leading-[0.95] text-ink sm:text-6xl">
            {title}
          </h1>
          <p className="max-w-xl text-lg leading-8 text-ink/70">{subtitle}</p>
          <Link
            to="/login"
            className="inline-flex text-sm font-semibold text-ink/70 transition hover:text-ink"
          >
            {t("common.backToLogin")}
          </Link>
        </section>

        <Panel className="relative p-6 sm:p-8">
          {children}
          {footer ? <div className="mt-6 border-t border-ink/10 pt-5">{footer}</div> : null}
        </Panel>
      </div>
    </main>
  );
}
